import { useCallback, useMemo, useState } from "react"
import { toast } from "sonner"
import { GenerateWithModelLabel } from "@/components/media/generate-with-model-label"
import { FlowReorderableSlots } from "@/components/flow/flow-reorderable-slots"
import type { FlowSidePanelProps } from "@/components/flow/flow-side-panel-props"
import { flowSlotSourceLabel } from "@/components/flow/flow-slot-chrome"
import { FLOW_NODE_KIND_ICON } from "@/components/flow/flow-node-title"
import { AgentColumnHeader } from "@/components/workspace/agent-column-header"
import { useGenerationPrice } from "@/hooks/use-generation-price"
import { useTranslation } from "@/hooks/use-translation"
import { confirmDelayKey } from "@/lib/confirm-delay-registry"
import type { FlowSlotMove } from "@/lib/flow-slot-drag"
import { runFlowChain, updateFlowNode } from "@/lib/flow-api"
import { flowNodePending, type FlowNodeInput } from "@/types/flow"

const NO_INPUTS: readonly FlowNodeInput[] = []

export function FlowChainPanel({
  node,
  nodes,
  projectId,
  frameFormat,
  frameRate,
  canEdit,
  onDisconnectEdge,
  onNodeChange,
  onAttach,
}: FlowSidePanelProps) {
  const { t } = useTranslation("flow")
  const [busy, setBusy] = useState(false)
  const [trackedNodeId, setTrackedNodeId] = useState(node.id)
  const inputs = node.inputs ?? NO_INPUTS
  const [shownInputs, setShownInputs] = useState(inputs)
  if (trackedNodeId !== node.id) {
    setTrackedNodeId(node.id)
    setShownInputs(inputs)
  }
  const slots = node.chain_slots ?? []
  const pending = flowNodePending(node)
  const missing = node.chain_id == null
  const broken = Boolean(node.chain_interface_error)
  const filled = slots.every((slot) => shownInputs.some((input) => input.slot_index === slot.position))
  const unassigned = shownInputs.filter((input) => input.slot_index == null)
  const priceSources = useMemo(
    () =>
      shownInputs
        .map((input) => {
          const source = nodes.find((row) => row.id === input.source_node_id)
          return `${input.slot_index ?? ""}:${input.source_node_id}:${source?.duration_ms ?? ""}`
        })
        .join(","),
    [nodes, shownInputs],
  )
  const price = useGenerationPrice({
    kind: "flow_chain",
    projectId,
    enabled: !missing && !broken && !pending && !busy,
    params: {
      node_id: node.id,
      sources: priceSources,
      quote_key: node.chain_quote_key ?? "",
    },
  })
  const locked = !canEdit || pending || busy
  const reorderLocked = locked || missing || broken
  const untitled = t("node.untitled")
  const disconnectLabel = t("chain.disconnect")
  const showShownInputs = useCallback((next: readonly FlowNodeInput[]) => {
    setShownInputs(next)
  }, [])

  const changeSlot = async (patch: FlowSlotMove) => {
    setBusy(true)
    try {
      onNodeChange(await updateFlowNode(projectId, node.id, patch))
    } finally {
      setBusy(false)
    }
  }

  const attachToSlot = async (slot: { position: number }, batch: Parameters<typeof onAttach>[0]) => {
    if (reorderLocked) return null
    setBusy(true)
    try {
      return await onAttach({ ...batch, slotIndex: slot.position })
    } finally {
      setBusy(false)
    }
  }

  const run = async () => {
    if (locked || missing || broken || !filled) return
    setBusy(true)
    try {
      onNodeChange(await runFlowChain(projectId, node.id))
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : t("errors.generateFailed"))
    } finally {
      setBusy(false)
    }
  }

  const title = node.title?.trim() || untitled

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <AgentColumnHeader icon={FLOW_NODE_KIND_ICON.chain} title={title} />
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden p-3">
        <FlowReorderableSlots
          listKey={node.id}
          className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto"
          slots={slots}
          inputs={inputs}
          nodes={nodes}
          projectId={projectId}
          frameFormat={frameFormat}
          frameRate={frameRate}
          canEdit={canEdit}
          locked={locked}
          reorderLocked={reorderLocked}
          emptyHint={t("chain.attachHint")}
          leading={
            <>
              {missing ? <p className="text-sm text-ui-text-muted">{t("node.chainMissing")}</p> : null}
              {node.chain_interface_error ? <p className="text-sm text-red-600">{node.chain_interface_error}</p> : null}
            </>
          }
          onDisconnect={onDisconnectEdge}
          onAttach={attachToSlot}
          onCommit={changeSlot}
          onShownInputs={showShownInputs}
        >
          {unassigned.map((input) => (
            <div key={input.id} className="flex items-center justify-between gap-2 text-sm">
              <span className="truncate text-ui-text-secondary">
                {flowSlotSourceLabel(
                  nodes.find((row) => row.id === input.source_node_id),
                  untitled,
                )}
              </span>
              <button
                type="button"
                className="shrink-0 text-xs text-ui-text-muted hover:text-ui-text-primary"
                disabled={locked}
                onClick={() => onDisconnectEdge(input.id)}
              >
                {disconnectLabel}
              </button>
            </div>
          ))}
        </FlowReorderableSlots>
        <GenerateWithModelLabel
          label={t("chain.run")}
          busyLabel={t("chain.running")}
          cost={price}
          busy={pending || busy}
          disabled={node.batch_waiting === true || locked || missing || broken || !filled}
          disabledReason={node.batch_waiting ? t("bulk.queued") : undefined}
          onClick={run}
          confirmDelayKey={confirmDelayKey("flow", node.id, "run")}
          size="lg"
          // Высота кнопки h-12 плюс строка цены: слот занят сразу, кнопка не поднимается, когда сумма появляется.
          className="min-h-[calc(3rem+0.25rem+13px)] w-full shrink-0 items-stretch"
          buttonClassName="w-full"
        />
      </div>
    </div>
  )
}
